/**
 * Shared sanitation for time ranges that come from pixel-derived user gestures
 * (uPlot native drag-zoom, custom box zoom) or from URL search params.
 *
 * Why this exists: `TimeSeriesService.getInterval` floors at
 * `{ interval: 1, unit: SECONDS }` for any input <= 1 seconds -- including 0 and
 * negative. So a zero-width or inverted range still produces a *valid-looking*
 * 1-second request that returns zero points, and the chart silently goes blank.
 * Every path that commits a range must run it through `clampTimeRange` first.
 */

/**
 * Absolute smallest zoom window we will ever commit, in milliseconds.
 *
 * This is a sanity bound on the *request*, not a judgement about usefulness:
 * `getInterval` buckets at 1 second minimum, so a window under a minute is at
 * most 60 buckets, and a window of zero is indistinguishable from a stray click
 * or a 1-pixel drag. The bound that decides whether a window is worth looking at
 * is `minSpanForSampleIntervalMs`, which is derived from the data, not fixed
 * here.
 */
export const ABSOLUTE_MIN_TIME_RANGE_SPAN_MS = 60_000

/**
 * Sample interval assumed when the loaded data cannot tell us its own cadence.
 *
 * Measured, not guessed: PI tags on this deployment log once every ~60s (median
 * gap 60.0s on `longridge-01-VT-BFW240F` across 6h/1d/3d/7d/30d windows).
 */
export const DEFAULT_SAMPLE_INTERVAL_MS = 60_000

/**
 * How many real samples a window must be able to hold to be worth committing.
 *
 * Why a sample count and not a duration: the question "is this zoomed in too
 * far?" has nothing to do with elapsed time and everything to do with how many
 * times the data changed inside the window. A 1-minute window on a tag that logs
 * every 60s contains ~1 point -- technically a valid request, and it renders a
 * blank or single-dot chart. The same 1-minute window on a 1s tag is a rich
 * view.
 *
 * Why 10: two points make a line, but a line drawn through 2-3 samples is mostly
 * interpolation -- you are reading the renderer, not the process. At ~10 samples
 * across the plot width the shape on screen is carried by measurements. This is
 * a floor on the *gesture*, not a promise about the data: a window that holds 10
 * samples' worth of time may still be sparse if the tag stopped logging, and the
 * chart shows that honestly.
 */
export const MIN_SAMPLES_PER_WINDOW = 10

/**
 * Ceiling on the derived floor, in milliseconds.
 *
 * A cadence estimate can only be as good as the data behind it: a tag that went
 * quiet for a day looks slow. Without this cap a bad estimate would lock the
 * user out of zooming at all, which is a far worse failure than allowing a
 * sparse window. One day is well past any cadence this page charts, so it only
 * ever catches a broken estimate.
 */
export const MAX_DERIVED_MIN_SPAN_MS = 24 * 60 * 60 * 1000

/**
 * The narrowest window that can hold `MIN_SAMPLES_PER_WINDOW` samples at the
 * given cadence, bounded at both ends.
 *
 * Pass the cadence measured from the loaded data (see
 * `sampleCadence.estimateSampleIntervalMs`); pass nothing when it is unknown and
 * the measured PI default is used.
 */
export function minSpanForSampleIntervalMs(
  sampleIntervalMs: number | null | undefined = DEFAULT_SAMPLE_INTERVAL_MS
): number {
  const cadence =
    sampleIntervalMs !== null &&
    sampleIntervalMs !== undefined &&
    Number.isFinite(sampleIntervalMs) &&
    sampleIntervalMs > 0
      ? sampleIntervalMs
      : DEFAULT_SAMPLE_INTERVAL_MS

  return Math.min(
    MAX_DERIVED_MIN_SPAN_MS,
    Math.max(ABSOLUTE_MIN_TIME_RANGE_SPAN_MS, cadence * MIN_SAMPLES_PER_WINDOW)
  )
}

/**
 * Default floor: what a 60s PI tag needs to fill a window. Callers that know the
 * cadence of what is actually on screen should pass their own `minSpanMs`.
 */
export const DEFAULT_MIN_TIME_RANGE_SPAN_MS = minSpanForSampleIntervalMs()

/**
 * Largest zoom window we will commit, in milliseconds.
 *
 * Justification: the widest preset offered in the UI is "Last 3 Months", and
 * zoom is a *narrowing* gesture, so this is purely a sanity bound against a
 * garbage pixel->value conversion (e.g. an un-initialized scale yielding an
 * epoch-0 min). 5 years is ~20x the widest legitimate range, so it never clips a
 * real user zoom.
 */
export const MAX_TIME_RANGE_SPAN_MS = 5 * 365 * 24 * 60 * 60 * 1000

export interface ClampTimeRangeOptions {
  /** Injectable for tests. Defaults to the current wall clock. */
  now?: number
  /**
   * Narrowest committable span. Defaults to the floor for a 60s PI tag; derive
   * it from the loaded data with `minSpanForSampleIntervalMs` where possible.
   */
  minSpanMs?: number
  maxSpanMs?: number
}

export interface ClampedTimeRange {
  /** Epoch milliseconds. Guaranteed `from < to`. */
  from: number
  /** Epoch milliseconds. Guaranteed `to <= now`. */
  to: number
}

/**
 * Normalize a candidate time range into one that is always safe to request.
 *
 * Guarantees on a non-null result:
 *   - `from` and `to` are finite integers and `from < to`
 *   - `to <= now` (no requests for data that cannot exist yet)
 *   - `minSpanMs <= to - from <= maxSpanMs`
 *
 * Returns `null` only when the input is not a range at all (non-finite), so the
 * caller can skip committing rather than guess.
 *
 * Normal zooms pass through untouched: a range that already satisfies the
 * guarantees is returned as-is (modulo rounding to whole milliseconds).
 */
export function clampTimeRange(
  fromMs: number,
  toMs: number,
  options: ClampTimeRangeOptions = {}
): ClampedTimeRange | null {
  const {
    now = Date.now(),
    minSpanMs = DEFAULT_MIN_TIME_RANGE_SPAN_MS,
    maxSpanMs = MAX_TIME_RANGE_SPAN_MS,
  } = options

  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return null

  // 1. Inverted input is a gesture artifact (drag right-to-left, swapped URL
  //    params), not a user intent to invert time. Order it rather than reject.
  let from = Math.min(fromMs, toMs)
  let to = Math.max(fromMs, toMs)

  // 2. Absurdly wide range: anchor on `to` so we keep the most recent window.
  if (to - from > maxSpanMs) {
    from = to - maxSpanMs
  }

  // 3. Too narrow to hold enough real samples (or zero-width / a 1-pixel drag):
  //    expand symmetrically about the midpoint so the instant the user actually
  //    pointed at stays centered.
  //
  //    Expand rather than refuse: refusing means the gesture does nothing with
  //    no explanation, and the user repeats it. Expanding always yields a
  //    readable chart, keeps what they pointed at in the middle, and the range
  //    it settled on is spelled out in the time range display -- the widening is
  //    visible, not silent. It is also stable under repetition: zooming in at
  //    the floor re-centers on the same midpoint and is a no-op.
  if (to - from < minSpanMs) {
    const midpoint = (from + to) / 2
    from = midpoint - minSpanMs / 2
    to = midpoint + minSpanMs / 2
  }

  // 4. Never ask for the future. Matches the existing arrow-nav convention in
  //    `activeTimeRangeHelpers.getNavigatedTimeRange`, which pins `newTo` to
  //    now. Shrinks the window rather than shifting it, so the right edge lands
  //    where the user aimed.
  if (to > now) {
    to = now
  }

  // 5. Step 4 (or a range that sat entirely in the future) may have pushed us
  //    back under the floor -- or inverted us. Re-establish the floor by
  //    extending backwards, which can never re-enter the future.
  if (to - from < minSpanMs) {
    from = to - minSpanMs
  }

  return { from: Math.round(from), to: Math.round(to) }
}
